import React, { useState } from 'react';
import { Phone, Mail } from 'lucide-react';
import { companyInfo } from '../../data/mock';
import { legalContent } from '../../data/legalContent';
import LegalModal from './LegalModal';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [activeModal, setActiveModal] = useState(null);

  const openModal = (modalType) => {
    setActiveModal(modalType);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setActiveModal(null);
    document.body.style.overflow = 'unset';
  };

  const footerLinks = {
    'Ürün': [
      { name: 'Özellikler', href: '#features' },
      { name: 'Fiyatlandırma', href: '#pricing' },
      { name: 'Demo Talep Et', href: '#contact' },
    ],
    'Destek': [
      { name: 'SSS', href: '#faq' },
      { name: 'İletişim', href: '#contact' },
    ],
    'Yasal': [
      { name: 'Gizlilik Politikası', modalType: 'privacyPolicy' },
      { name: 'Kullanım Koşulları', modalType: 'termsOfUse' },
      { name: 'KVKK', modalType: 'kvkk' },
    ],
  };

  const scrollToSection = (href) => {
    if (href && href.startsWith('#') && href.length > 1) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleLinkClick = (link) => {
    if (link.modalType) {
      openModal(link.modalType);
    } else if (link.href) {
      scrollToSection(link.href);
    }
  };

  const getModalContent = () => {
    if (!activeModal) return null;
    return legalContent[activeModal];
  };

  const modalData = getModalContent();

  return (
    <>
      <footer className="relative bg-[#0D0B09] border-t border-[#C9A97B]/10">
        {/* Main Footer */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {/* Brand Column */}
            <div className="col-span-2">
              <a href="#" className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#C9A97B] to-[#A68B5B] flex items-center justify-center">
                  <span className="text-[#1A1512] font-bold text-lg font-display">T</span>
                </div>
                <div>
                  <span className="text-xl font-display font-semibold text-gold-gradient">
                    {companyInfo.name}
                  </span>
                  <p className="text-[10px] text-[#7A7067] tracking-wider uppercase">
                    {companyInfo.slogan}
                  </p>
                </div>
              </a>
              <p className="text-[#7A7067] text-sm mb-6 max-w-xs">
                Kuyumculuk sektörünün en kapsamlı dijital çözümü. 
                Canlı borsa, hibrit stok ve akıllı CRM tek platformda.
              </p>
              
              {/* Contact Info */}
              <div className="space-y-3">
                <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 text-[#BFB5A5] hover:text-[#C9A97B] text-sm transition-colors">
                  <Phone className="w-4 h-4 text-[#C9A97B]" />
                  {companyInfo.phone}
                </a>
                <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-2 text-[#BFB5A5] hover:text-[#C9A97B] text-sm transition-colors">
                  <Mail className="w-4 h-4 text-[#C9A97B]" />
                  {companyInfo.email}
                </a>
              </div>
            </div>

            {/* Link Columns */}
            {Object.entries(footerLinks).map(([category, links]) => (
              <div key={category}>
                <h4 className="text-[#F5F0E8] font-display font-semibold mb-4">{category}</h4>
                <ul className="space-y-3">
                  {links.map((link) => (
                    <li key={link.name}>
                      <button
                        onClick={() => handleLinkClick(link)}
                        className="text-[#7A7067] hover:text-[#C9A97B] text-sm transition-colors text-left"
                        data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {link.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#C9A97B]/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-[#7A7067] text-sm">
                © {currentYear} YS SOFTWARE. Tüm hakları saklıdır.
              </p>
              
              {/* Payment/Security Badges */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1A1512] border border-[#C9A97B]/10">
                  <svg className="w-4 h-4 text-[#C9A97B]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-[#7A7067] text-xs">SSL Güvenli</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1A1512] border border-[#C9A97B]/10">
                  <svg className="w-4 h-4 text-[#C9A97B]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-[#7A7067] text-xs">KVKK Uyumlu</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Modal */}
      <LegalModal
        isOpen={!!activeModal}
        onClose={closeModal}
        title={modalData?.title || ''}
        content={modalData?.content || ''}
      />
    </>
  );
};

export default Footer;
